import { FormEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Reservation } from '../contexts/ReservationContext.tsx';
import { ReservationCustomerStatusChoice } from '../components/admin/ReservationFormClientConfirmationModal.tsx';
import {
  CapacityWarningInfo,
  ReservationFormDataShape,
  toNumberOrNull,
  getGuestCapacityWarning,
  extractErrorMessage,
  isReservationSaveValidationError,
  buildReservationPayload,
  formatDateKey,
} from '../components/admin/reservationForm.utils.ts';

type UseReservationFormSubmitArgs = {
  isEditing: boolean;
  reservation?: any;
  formData: ReservationFormDataShape;
  salaInfo: any;
  salaUsaMesas: boolean;
  guestValidationError: string;
  guestCapacityWarning: CapacityWarningInfo | null;
  guestCapacityWarningConfirmed: boolean;
  setGuestValidationError: (value: string) => void;
  setGuestCapacityWarning: (value: CapacityWarningInfo | null) => void;
  runGuestValidation: (guests: number, tableIds?: Array<string | number>) => string;
  selectedTurno: number | null;
  selectedHorarioId: number | null;
  requiresRestaurantConfirmation: boolean;
  requiresClientConfirmation: boolean;
  createReservation: (data: Reservation) => Promise<any>;
  updateReservation: (id: string | number, data: Partial<Reservation>) => Promise<any>;
  onClose: () => void;
  onReservationCancelled?: (reservationId: string | number) => Promise<void> | void;
};

export default function useReservationFormSubmit({
  isEditing,
  reservation,
  formData,
  salaInfo,
  salaUsaMesas,
  guestValidationError,
  guestCapacityWarning,
  guestCapacityWarningConfirmed,
  setGuestValidationError,
  setGuestCapacityWarning,
  runGuestValidation,
  selectedTurno,
  selectedHorarioId,
  requiresRestaurantConfirmation,
  requiresClientConfirmation,
  createReservation,
  updateReservation,
  onClose,
  onReservationCancelled,
}: UseReservationFormSubmitArgs) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [pendingReservationPayload, setPendingReservationPayload] = useState<Reservation | null>(null);
  const [showClientConfirmationChoice, setShowClientConfirmationChoice] = useState(false);
  const [isSavingReservation, setIsSavingReservation] = useState(false);

  const isReservationForToday = useCallback((date: Date | null | undefined) => (
    formatDateKey(date) === formatDateKey(new Date())
  ), []);

  const notifySaveError = (error: any) => {
    const errorMessage = extractErrorMessage(error, 'Erro ao salvar reserva');
    if (isReservationSaveValidationError(errorMessage)) {
      toast.warning(errorMessage);
    } else {
      toast.error(errorMessage);
    }
    return errorMessage;
  };

  const saveReservation = useCallback(async (reservationData: Reservation) => {
    setIsSavingReservation(true);

    try {
      if (isEditing) {
        await updateReservation(reservation.id, reservationData);
        toast.success('Reserva atualizada com sucesso.');
      } else {
        await createReservation(reservationData);
        toast.success('Reserva criada com sucesso.');
      }

      setShowClientConfirmationChoice(false);
      setPendingReservationPayload(null);
      onClose();
      return true;
    } catch (error: any) {
      notifySaveError(error);
      setShowClientConfirmationChoice(false);
      setPendingReservationPayload(null);
      return false;
    } finally {
      setIsSavingReservation(false);
    }
  }, [createReservation, isEditing, onClose, reservation?.id, updateReservation]);

  const closeClientConfirmationChoiceModal = useCallback(() => {
    if (isSavingReservation) return;
    setShowClientConfirmationChoice(false);
    setPendingReservationPayload(null);
  }, [isSavingReservation]);

  const handleClientConfirmationChoice = useCallback(async (
    statusCliente: ReservationCustomerStatusChoice
  ) => {
    if (!pendingReservationPayload) return;
    await saveReservation({
      ...pendingReservationPayload,
      status_cliente: statusCliente,
    });
  }, [pendingReservationPayload, saveReservation]);

  const pendingReservationHasPhone = Boolean(
    String(pendingReservationPayload?.telefone_cliente || '').trim()
  );

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const guestCountForSubmission = toNumberOrNull(formData.guests) ?? 0;
    const selectedTableIds = formData.tableIds.map((tableId) => String(tableId)).filter(Boolean);

    if (salaInfo) {
      const validationMessage = runGuestValidation(guestCountForSubmission, selectedTableIds);
      const warning = getGuestCapacityWarning(validationMessage);
      setGuestCapacityWarning(warning);
      setGuestValidationError(warning ? '' : validationMessage);
      if (warning) {
        if (!guestCapacityWarningConfirmed) {
          toast.warning(warning.message);
          return;
        }
      } else if (validationMessage) {
        toast.warning(validationMessage);
        return;
      }
    }

    if (guestValidationError) {
      toast.warning(guestValidationError);
      return;
    }

    if (guestCapacityWarning && !guestCapacityWarningConfirmed) {
      toast.warning(guestCapacityWarning.message);
      return;
    }

    if (salaUsaMesas && selectedTableIds.length === 0) {
      toast.warning('Por favor, selecione ao menos uma mesa disponível antes de continuar.');
      return;
    }

    const reservationData = buildReservationPayload({
      formData,
      guestCount: guestCountForSubmission,
      salaUsaMesas,
      selectedTableIds,
      turno: selectedTurno,
      horarioId: selectedHorarioId,
      guestCapacityWarningConfirmed,
      requiresRestaurantConfirmation,
      isEditing,
    });

    if (!isEditing && requiresClientConfirmation && !isReservationForToday(formData.date)) {
      setPendingReservationPayload(reservationData);
      setShowClientConfirmationChoice(true);
      return;
    }

    try {
      if (isEditing) {
        await updateReservation(reservation.id, reservationData);
        toast.success('Reserva atualizada com sucesso.');
      } else {
        await createReservation(reservationData);
        toast.success('Reserva criada com sucesso.');
      }
      onClose();
    } catch (error: any) {
      notifySaveError(error);
    }
  }, [
    createReservation,
    formData,
    guestCapacityWarning,
    guestCapacityWarningConfirmed,
    guestValidationError,
    isEditing,
    isReservationForToday,
    onClose,
    requiresClientConfirmation,
    requiresRestaurantConfirmation,
    reservation?.id,
    runGuestValidation,
    salaInfo,
    salaUsaMesas,
    selectedHorarioId,
    selectedTurno,
    setGuestCapacityWarning,
    setGuestValidationError,
    updateReservation,
  ]);

  const handleCancelReservation = useCallback(async () => {
    if (!reservation?.id) return;

    setIsCanceling(true);
    try {
      await updateReservation(reservation.id, { status: 'cancelada' });
      try {
        await onReservationCancelled?.(reservation.id);
      } catch (cleanupError) {
        console.error('Erro ao liberar mesa vinculada após cancelar reserva:', cleanupError);
        toast.error('Reserva cancelada, mas não foi possível liberar a mesa vinculada.');
      }
      toast.success('Reserva cancelada com sucesso.');
      setShowCancelConfirm(false);
      onClose();
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Erro ao cancelar reserva'));
    } finally {
      setIsCanceling(false);
    }
  }, [onClose, onReservationCancelled, reservation?.id, updateReservation]);

  return {
    showCancelConfirm,
    setShowCancelConfirm,
    isCanceling,
    pendingReservationPayload,
    showClientConfirmationChoice,
    isSavingReservation,
    pendingReservationHasPhone,
    closeClientConfirmationChoiceModal,
    handleClientConfirmationChoice,
    handleSubmit,
    handleCancelReservation,
  };
}
